/** Quick changes: hours (S70) and the emergency banner (S72). docs/52 §3.8, §3.9. */
import { Banner, Button, Card, ChoiceGroup, Icon, Switch, TextField } from '@ff/app-ui';
import type { quick as K } from '@ff/contracts';
import type { JSX } from 'preact';
import { useEffect, useState } from 'preact/hooks';
import type { z } from 'zod';
import { errorText } from '../lib/api.ts';
import { invalidate, send, useQuery, useSend } from '../lib/data.ts';
import { clockLabel } from '../lib/format.ts';
import { location } from '../lib/router.ts';
import { useStore } from '../lib/store.ts';
import { haptic, toast } from '../lib/ui.ts';
import { S } from '@ff/app-ui/copy';
import { Loaded, Screen, Section } from '../ui/common.tsx';

type Day = z.infer<typeof K.DayHours>;
type Closure = { from: string; to: string; label?: string };

function dayText(d: Day): string {
  if (!d.open) return S.hours.closed;
  if (d.allDay) return S.hours.allDay;
  return d.intervals.map((i) => `${clockLabel(i.start)} to ${clockLabel(i.end)}`).join(', ');
}

export function HoursScreen(): JSX.Element {
  const loc = useStore(location);
  const q = useQuery('hours', { query: loc.scope ? { location: loc.scope } : {} });
  return (
    <Screen title={S.hours.title} back="/account" lead={S.hours.lead} query={q}>
      <Loaded q={q} skeleton="detail">
        {(h) => <HoursForm key={h.locationId ?? 'all'} initial={h.weekly} closures={h.closures} pending={!!h.pendingRequestId} />}
      </Loaded>
    </Screen>
  );
}

function HoursForm({ initial, closures: initialClosures, pending }: { initial: Day[]; closures: Closure[]; pending: boolean }): JSX.Element {
  const [week, setWeek] = useState<Day[]>(initial);
  const [closures, setClosures] = useState<Closure[]>(initialClosures.map(({ from, to, label }) => ({ from, to, ...(label ? { label } : {}) })));
  const [errors, setErrors] = useState<Record<string, string>>({});
  const act = useSend();
  const loc = useStore(location);
  useEffect(() => setWeek(initial), [JSON.stringify(initial)]);
  const set = (i: number, patch: Partial<Day>) => setWeek((w) => w.map((d, j) => (j === i ? { ...d, ...patch } : d)));
  const changed = week.map((d, i) => ({ d, before: initial[i]! })).filter(({ d, before }) => dayText(d) !== dayText(before));
  const closuresChanged = JSON.stringify(closures) !== JSON.stringify(initialClosures.map(({ from, to, label }) => ({ from, to, ...(label ? { label } : {}) })));

  const submit = async (e: Event) => {
    e.preventDefault();
    const errs: Record<string, string> = {};
    week.forEach((d) => {
      if (d.open && !d.allDay) d.intervals.forEach((iv) => iv.start >= iv.end && (errs[d.day] = S.hours.endAfterStart));
    });
    closures.forEach((c, i) => c.from > c.to && (errs[`c${i}`] = S.hours.endAfterStart));
    setErrors(errs);
    if (Object.keys(errs).length) return;
    const out = await act.run(() => send('hoursUpdate', { body: { weekly: week, closures, ...(loc.scope ? { locationId: loc.scope } : {}) } }));
    if (!out) return;
    haptic();
    invalidate('hours', 'inbox');
    toast(S.hours.sent(out.promisedLabel));
  };

  return (
    <form class="stack form-narrow" data-gap="l" onSubmit={submit} noValidate>
      {pending && <Banner tone="info" title={S.hours.pending} />}
      <ul role="list" class="hours">
        {week.map((d, i) => (
          <li key={d.day} class="hours-day">
            <Switch
              label={<strong>{S.hours.day[d.day]}</strong>}
              detail={dayText(d)}
              checked={d.open}
              onChange={(open) => set(i, { open, intervals: open && !d.intervals.length ? [{ start: '08:00', end: '17:00' }] : d.intervals })}
            />
            {d.open && (
              <div class="stack" data-gap="s">
                <label class="check">
                  <input type="checkbox" checked={d.allDay} onChange={(e) => set(i, { allDay: (e.currentTarget as HTMLInputElement).checked })} />
                  <span>{S.hours.allDay}</span>
                </label>
                {!d.allDay &&
                  d.intervals.map((iv, k) => (
                    <div class="time-pair" key={k}>
                      <TextField label={`${S.hours.from}, ${S.hours.day[d.day]}`} type="time" value={iv.start} onInput={(v) => set(i, { intervals: d.intervals.map((x, j) => (j === k ? { ...x, start: v } : x)) })} />
                      <TextField label={`${S.hours.to}, ${S.hours.day[d.day]}`} type="time" value={iv.end} onInput={(v) => set(i, { intervals: d.intervals.map((x, j) => (j === k ? { ...x, end: v } : x)) })} error={errors[d.day] ?? null} />
                    </div>
                  ))}
              </div>
            )}
          </li>
        ))}
      </ul>

      <Section title={S.hours.closures} id="closures">
        <div class="stack" data-gap="m">
          {closures.map((c, i) => (
            <Card key={i}>
              <div class="time-pair">
                <TextField label={S.hours.closureFrom} type="date" value={c.from} onInput={(v) => setClosures((cs) => cs.map((x, j) => (j === i ? { ...x, from: v, to: x.to < v ? v : x.to } : x)))} />
                <TextField label={S.hours.closureTo} type="date" value={c.to} min={c.from} onInput={(v) => setClosures((cs) => cs.map((x, j) => (j === i ? { ...x, to: v } : x)))} error={errors[`c${i}`] ?? null} />
              </div>
              <TextField label={S.hours.closureLabel} value={c.label ?? ''} maxLength={60} onInput={(v) => setClosures((cs) => cs.map((x, j) => (j === i ? { ...x, ...(v ? { label: v } : { label: undefined }) } : x)))} />
              <Button variant="quiet" icon="trash" onClick={() => setClosures((cs) => cs.filter((_, j) => j !== i))}>
                {S.app.remove}
              </Button>
            </Card>
          ))}
          <Button icon="plus" onClick={() => setClosures((cs) => [...cs, { from: new Date().toISOString().slice(0, 10), to: new Date().toISOString().slice(0, 10) }])}>
            {S.hours.addClosure}
          </Button>
        </div>
      </Section>

      <Section title={S.hours.summary} id="hours-summary">
        {changed.length || closuresChanged ? (
          <ul class="bullets" aria-live="polite">
            {changed.map(({ d, before }) => (
              <li key={d.day}>{S.hours.changeLine(S.hours.day[d.day]!, dayText(d), dayText(before).toLowerCase())}</li>
            ))}
            {closuresChanged && <li>{S.hours.closures}</li>}
          </ul>
        ) : (
          <p class="t-muted">{S.hours.noChanges}</p>
        )}
      </Section>
      <p class="t-small t-muted">
        <Icon name="clock" size={16} /> {S.hours.sameDay}
      </p>
      {act.error && <Banner tone="danger" role="alert" title={errorText(act.error, S.errors.offlineSend)} />}
      <div class="sticky-submit">
        <Button type="submit" variant="primary" size="lg" block busy={act.busy} disabled={!changed.length && !closuresChanged}>
          {S.hours.send}
        </Button>
      </div>
    </form>
  );
}

export function BannerScreen(): JSX.Element {
  const q = useQuery('notices');
  const act = useSend();
  const [pick, setPick] = useState<z.infer<typeof K.NoticeTemplate> | null>(null);
  const tomorrow = new Date(Date.now() + 86_400_000).toISOString().slice(0, 10);
  const [until, setUntil] = useState(tomorrow);
  const [done, setDone] = useState(false);
  return (
    <Screen title={S.banner.title} back="/account" lead={S.banner.lead} query={q}>
      <Loaded q={q}>
        {(d) => (
          <form
            class="stack form-narrow"
            data-gap="l"
            onSubmit={async (e) => {
              e.preventDefault();
              if (!pick) return;
              const out = await act.run(() => send('noticeCreate', { body: { template: pick, showUntil: until } }));
              if (out) {
                haptic();
                setDone(true);
                invalidate('notices');
              }
            }}
          >
            {d.items.filter((n) => n.state === 'live').map((n) => (
              <Banner key={n.id} tone="ok" title={S.banner.live}>{n.text}</Banner>
            ))}
            <ChoiceGroup legend={S.banner.title} legendClass="sr-only" value={pick} onChange={setPick} options={d.templates.map((t) => ({ value: t.template, label: `"${t.preview}"` }))} />
            <TextField label={S.banner.until} type="date" min={new Date().toISOString().slice(0, 10)} value={until} onInput={setUntil} />
            {done && <Banner tone="ok" role="status" title={S.banner.sent} />}
            {act.error && <Banner tone="danger" role="alert" title={errorText(act.error, S.errors.offlineSend)} />}
            <Button type="submit" variant="primary" size="lg" block busy={act.busy} disabled={!pick || done}>
              {S.banner.send}
            </Button>
          </form>
        )}
      </Loaded>
    </Screen>
  );
}
